// AccountSettings.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Typography,
  Select,
  Button,
  Switch,
  Tooltip,
  Spin,
  message,
  Alert,
  Input,
  Modal
} from 'antd';
import {
  TranslationOutlined,
  QuestionCircleOutlined,
  FlagOutlined,
  BulbOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getAuth, verifyBeforeUpdateEmail, signOut } from 'firebase/auth';
import { LANGUAGE_CONFIG } from '../LanguageConfig';
import { useAccountContext } from '../Account';
import { shouldShowDollarEquivalents } from '../utils/costUtils';
import AccountAlwaysTranslateModal from './AccountAlwaysTranslateModal';
import AccountNeverTranslateModal from './AccountNeverTranslateModal';
import { useTheme } from '../theme/ThemeProvider';
import type { TourProps } from 'antd';
import AppTour, { renderAppTourPanel } from '../tours/AppTour';
import {
  ACCOUNT_SETTINGS_TOUR_EVENT,
  ACCOUNT_SETTINGS_TOUR_STORAGE_KEY
} from './accountTourEvents';
import './AccountSettings.css';

const { Title, Text } = Typography;
const { Option } = Select;

type WordsMap = Record<string, string[]>;
type DefaultLanguageMode = 'empty' | 'context' | 'specific';
type SettingStatus = 'saving' | 'saved' | 'error';
type SettingStatusKey =
  | 'defaultLanguageMode'
  | 'specificLanguage'
  | 'defaultBookTab'
  | 'translationWorkflow'
  | 'characterAnalysis'
  | 'termScan'
  | 'simpleMode'
  | 'theme'
  | 'saga'
  | 'flags'
  | 'dollar'
  | 'email'
  | 'tutorials';

const baseLanguages = LANGUAGE_CONFIG.baseLanguages;
const API_BASE = 'https://askpoeai-nswdzfky7q-uc.a.run.app';
//const API_BASE = 'http://localhost:8000';
const TRANSLATION_WORKFLOW_PREF_FLAG = 'translationWorkflowPreferAdvanced';
const TRANSLATION_AUTORUN_TERM_SCAN_FLAG = 'translationAutoRunTermScanBeforeBulk';
const TRANSLATION_REQUIRE_CHARACTER_ANALYSIS_FLAG = 'translationRequireCharacterAnalysis';
const SIMPLE_MODE_FLAG = 'simpleModeEnabled';
const BULK_TERM_SCAN_PREFLIGHT_SYSTEM_FLAG = 'bulkTranslationTermScanPreflightEnabled';
const SHOW_DOLLAR_EQUIVALENTS_SETTING = false;

interface SettingsSectionProps {
  title: string;
  kicker: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  tourId?: string;
  compact?: boolean;
}

interface SettingsGroupProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

interface SettingRowProps {
  title: string;
  description: React.ReactNode;
  icon?: React.ReactNode;
  tooltip?: React.ReactNode;
  children: React.ReactNode;
  helper?: React.ReactNode;
  status?: SettingStatus;
  tourId?: string;
  variant?: 'default' | 'warning' | 'danger' | 'muted';
}

interface SettingsSelectRowProps extends Omit<SettingRowProps, 'children'> {
  spinning?: boolean;
  children: React.ReactNode;
}

interface SettingsSwitchRowProps extends Omit<SettingRowProps, 'children'> {
  checked: boolean;
  onChange: (checked: boolean) => void | Promise<void>;
  disabled?: boolean;
  spinning?: boolean;
  ariaLabel: string;
}

interface SettingsActionRowProps extends Omit<SettingRowProps, 'children'> {
  buttonText: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  primary?: boolean;
  spinning?: boolean;
}

interface RulesManagerPanelProps {
  baseLanguages: string[];
  selectedRuleLanguage: string;
  setSelectedRuleLanguage: (language: string) => void;
  canEditRules: boolean;
  isAuthenticated: boolean;
  setShowAlwaysModal: (show: boolean) => void;
  setShowNeverModal: (show: boolean) => void;
}

interface AccountEmailPanelProps {
  currentEmail: string;
  newEmail: string;
  setNewEmail: (value: string) => void;
  isAuthenticated: boolean;
  isPasswordAccount: boolean;
  isUpdatingEmail: boolean;
  isLoadingInitial: boolean;
  emailStatus?: SettingStatus;
  handleSendEmailVerificationLink: () => void;
}

interface TutorialsPanelProps {
  isAuthenticated: boolean;
  isLoadingInitial: boolean;
  isResettingTutorials: boolean;
  tutorialsStatus?: SettingStatus;
  handleResetTutorials: () => void;
}

const settingStatusLabels: Record<SettingStatus, string> = {
  saving: 'Saving...',
  saved: 'Saved',
  error: 'Could not save',
};

function SettingsSection({
  title,
  kicker,
  description,
  icon,
  children,
  tourId,
  compact = false,
}: SettingsSectionProps): JSX.Element {
  return (
    <Card
      bordered
      className={`account-settings-section${compact ? ' account-settings-section--compact' : ''}`}
      data-tour-id={tourId}
      title={(
        <div className="account-settings-section-title">
          {icon && <span className="account-settings-section-icon">{icon}</span>}
          <div className="account-settings-section-copy">
            <span className="account-settings-kicker">{kicker}</span>
            <Title level={3}>{title}</Title>
            {description && <Text type="secondary">{description}</Text>}
          </div>
        </div>
      )}
    >
      <div className="account-settings-section-body">{children}</div>
    </Card>
  );
}

function SettingsGroup({ title, description, children }: SettingsGroupProps): JSX.Element {
  return (
    <div className="account-settings-group">
      <div className="account-settings-group-header">
        <Text strong>{title}</Text>
        {description && <Text type="secondary">{description}</Text>}
      </div>
      <div className="account-settings-group-body">{children}</div>
    </div>
  );
}

function SettingRow({
  title,
  description,
  icon,
  tooltip,
  children,
  helper,
  status,
  tourId,
  variant = 'default',
}: SettingRowProps): JSX.Element {
  return (
    <div
      className={`account-settings-row account-settings-row--${variant}`}
      data-tour-id={tourId}
    >
      <div className="account-settings-row-main">
        {icon && <span className="account-settings-row-icon">{icon}</span>}
        <div className="account-settings-row-copy">
          <div className="account-settings-row-title">
            <Text strong>{title}</Text>
            {tooltip && (
              <Tooltip title={tooltip}>
                <QuestionCircleOutlined className="account-settings-help-icon" />
              </Tooltip>
            )}
          </div>
          <Text type="secondary" className="account-settings-row-description">
            {description}
          </Text>
          {helper && (
            <Text
              type={variant === 'warning' ? 'warning' : variant === 'danger' ? 'danger' : 'secondary'}
              className="account-settings-row-helper"
            >
              {helper}
            </Text>
          )}
        </div>
      </div>
      <div className="account-settings-row-control">
        <div className="account-settings-control-stack">
          {children}
          {status && (
            <Text
              type={status === 'error' ? 'danger' : 'secondary'}
              className={`account-settings-save-status account-settings-save-status--${status}`}
            >
              {settingStatusLabels[status]}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsSelectRow({ spinning = false, children, ...rowProps }: SettingsSelectRowProps): JSX.Element {
  return (
    <SettingRow {...rowProps}>
      <Spin spinning={spinning}>
        <div className="account-settings-control-wrap">{children}</div>
      </Spin>
    </SettingRow>
  );
}

function SettingsSwitchRow({
  checked,
  onChange,
  disabled,
  spinning = false,
  ariaLabel,
  ...rowProps
}: SettingsSwitchRowProps): JSX.Element {
  return (
    <SettingRow {...rowProps}>
      <Spin spinning={spinning}>
        <Switch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          aria-label={ariaLabel}
        />
      </Spin>
    </SettingRow>
  );
}

function SettingsActionRow({
  buttonText,
  onClick,
  disabled,
  danger,
  primary,
  spinning = false,
  ...rowProps
}: SettingsActionRowProps): JSX.Element {
  return (
    <SettingRow {...rowProps}>
      <Spin spinning={spinning}>
        <Button
          type={primary ? 'primary' : 'default'}
          danger={danger}
          onClick={onClick}
          disabled={disabled}
          className="account-settings-action-button"
        >
          {buttonText}
        </Button>
      </Spin>
    </SettingRow>
  );
}

function RulesManagerPanel({
  baseLanguages,
  selectedRuleLanguage,
  setSelectedRuleLanguage,
  canEditRules,
  isAuthenticated,
  setShowAlwaysModal,
  setShowNeverModal,
}: RulesManagerPanelProps): JSX.Element {
  const rulesHelper = !isAuthenticated
    ? 'Sign in to manage account-level translation rules.'
    : !canEditRules
      ? 'Select a language before opening rule management.'
      : `${selectedRuleLanguage} rules apply across all books in your account.`;
  const disabledActionHint = !canEditRules || !isAuthenticated;

  return (
    <>
      <SettingsSelectRow
        title="Rule language"
        description="Choose the language for account-wide Always/Never Translate lists."
        helper={rulesHelper}
      >
        <Select
          className="account-settings-select"
          placeholder="Select language for translation rules"
          onChange={setSelectedRuleLanguage}
          value={selectedRuleLanguage}
          size="large"
          optionFilterProp="children"
          showSearch
          filterOption={(input, option) =>
            String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
          }
          disabled={!isAuthenticated}
        >
          {baseLanguages.map((language: string) => (
            <Option key={language} value={language}>
              {language}
            </Option>
          ))}
        </Select>
      </SettingsSelectRow>

      <div className="account-settings-action-grid">
        <Button
          type="default"
          icon={<TranslationOutlined />}
          onClick={() => setShowAlwaysModal(true)}
          disabled={!canEditRules || !isAuthenticated}
          size="large"
          className="account-settings-action-button"
        >
          Manage Always Translate Rules
        </Button>

        <Button
          type="default"
          icon={<TranslationOutlined />}
          onClick={() => setShowNeverModal(true)}
          disabled={!canEditRules || !isAuthenticated}
          size="large"
          className="account-settings-action-button"
        >
          Manage Never Translate Rules
        </Button>
      </div>
      {disabledActionHint && (
        <Text type="secondary" className="account-settings-inline-status">
          {isAuthenticated ? 'Select a language first.' : 'Sign in to manage rules.'}
        </Text>
      )}
    </>
  );
}

function AccountEmailPanel({
  currentEmail,
  newEmail,
  setNewEmail,
  isAuthenticated,
  isPasswordAccount,
  isUpdatingEmail,
  isLoadingInitial,
  emailStatus,
  handleSendEmailVerificationLink,
}: AccountEmailPanelProps): JSX.Element {
  const trimmedEmail = newEmail.trim();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailInvalid = trimmedEmail.length > 0 && !emailPattern.test(trimmedEmail);
  const emailHelper = !isPasswordAccount
    ? 'Google sign-in accounts cannot be changed here.'
    : trimmedEmail.length === 0
      ? 'Enter a new email.'
      : emailInvalid
        ? 'Enter a valid email address.'
        : 'Verification will be required before the email changes.';

  return (
    <>
      <SettingRow
        title="Current email"
        description="This is the email currently tied to your ScribeShadow account."
        variant="muted"
      >
        <Text className="account-settings-value">{currentEmail || 'Unknown'}</Text>
      </SettingRow>

      <SettingsSelectRow
        title="New email"
        description="Enter the new address and send a verification link."
        helper={emailHelper}
        variant={!isPasswordAccount ? 'warning' : emailInvalid ? 'danger' : 'default'}
        spinning={isUpdatingEmail}
      >
        <Input
          className="account-settings-input"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="name@example.com"
          disabled={!isAuthenticated || !isPasswordAccount || isUpdatingEmail || isLoadingInitial}
        />
      </SettingsSelectRow>

      <SettingsActionRow
        title="Verify change"
        description="Send a verification link to the new address."
        helper={trimmedEmail.length === 0 && isPasswordAccount ? 'Enter a new email.' : emailInvalid ? 'Enter a valid email address.' : undefined}
        variant={emailInvalid ? 'danger' : 'default'}
        status={emailStatus}
        buttonText="Send verification link"
        onClick={handleSendEmailVerificationLink}
        disabled={!isAuthenticated || !isPasswordAccount || isUpdatingEmail || isLoadingInitial || !trimmedEmail || emailInvalid}
        primary
        spinning={isUpdatingEmail}
      />

      {!isPasswordAccount && (
        <Alert
          type="warning"
          showIcon
          message="Google sign-in account"
          description="Your account uses Google sign-in and cannot be changed."
          className="account-settings-alert"
        />
      )}
      {isPasswordAccount && trimmedEmail.length > 0 && !emailInvalid && (
        <Alert
          type="info"
          showIcon
          message="How this works"
          description="We'll send a verification link to the new address and you'll be signed out. Open the email to verify your new address, then sign back in with your new email."
          className="account-settings-alert"
        />
      )}
    </>
  );
}

function TutorialsPanel({
  isAuthenticated,
  isLoadingInitial,
  isResettingTutorials,
  tutorialsStatus,
  handleResetTutorials,
}: TutorialsPanelProps): JSX.Element {
  return (
    <SettingsActionRow
      title="Reset all tours"
      description="Clear saved tour state so all tours automatically show again."
      buttonText="Reset tours"
      onClick={handleResetTutorials}
      disabled={!isAuthenticated || isLoadingInitial}
      danger
      spinning={isResettingTutorials || isLoadingInitial}
      status={tutorialsStatus}
      variant="muted"
    />
  );
}

export default function AccountSettings(): JSX.Element {
  const { usersSharedState } = useAccountContext();
  const userID: string = usersSharedState?.userID || '';
  const navigate = useNavigate();
  const { isDark, setMode } = useTheme();

  // State variables
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [defaultLanguageMode, setDefaultLanguageMode] = useState<DefaultLanguageMode>('specific');
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState<boolean>(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>(false);
  const [defaultBookTab, setDefaultBookTab] = useState<string>('dashboard');
  const [isUpdatingDefaultBookTab, setIsUpdatingDefaultBookTab] = useState<boolean>(false);

  // Account email state
  const [currentEmail, setCurrentEmail] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState<boolean>(false);
  const [isPasswordAccount, setIsPasswordAccount] = useState<boolean>(true);

  const [selectedRuleLanguage, setSelectedRuleLanguage] = useState<string>('');
  const [alwaysWords, setAlwaysWords] = useState<WordsMap>({});
  const [neverWords, setNeverWords] = useState<WordsMap>({});
  const [showAlwaysModal, setShowAlwaysModal] = useState<boolean>(false);
  const [showNeverModal, setShowNeverModal] = useState<boolean>(false);

  const [showTranslatedFlags, setShowTranslatedFlags] = useState<boolean>(true);
  const [isUpdatingFlags, setIsUpdatingFlags] = useState<boolean>(false);

  const [isUpdatingTheme, setIsUpdatingTheme] = useState<boolean>(false);

  // Saga Raven preference
  const [showSagaRaven, setShowSagaRaven] = useState<boolean>(true);
  const [isUpdatingSaga, setIsUpdatingSaga] = useState<boolean>(false);

  const [isResettingTutorials, setIsResettingTutorials] = useState<boolean>(false);
  // Dollar equivalents preference
  const [showDollarEquivalents, setShowDollarEquivalents] = useState<boolean>(true);
  const [isUpdatingDollar, setIsUpdatingDollar] = useState<boolean>(false);
  const [translationWorkflowPreferAdvanced, setTranslationWorkflowPreferAdvanced] = useState<boolean>(false);
  const [translationRequireCharacterAnalysis, setTranslationRequireCharacterAnalysis] = useState<boolean>(true);
  const [translationAutoRunTermScanBeforeBulk, setTranslationAutoRunTermScanBeforeBulk] = useState<boolean>(false);
  const [simpleModeEnabled, setSimpleModeEnabled] = useState<boolean>(false);
  const [bulkTermScanPreflightEnabled, setBulkTermScanPreflightEnabled] = useState<boolean>(true);
  const [isUpdatingWorkflowModeDefault, setIsUpdatingWorkflowModeDefault] = useState<boolean>(false);
  const [isUpdatingCharacterAnalysisDefault, setIsUpdatingCharacterAnalysisDefault] = useState<boolean>(false);
  const [isUpdatingTermScanAutoRun, setIsUpdatingTermScanAutoRun] = useState<boolean>(false);
  const [isUpdatingSimpleMode, setIsUpdatingSimpleMode] = useState<boolean>(false);
  const [settingStatuses, setSettingStatuses] = useState<Partial<Record<SettingStatusKey, SettingStatus>>>({});

  // Guided tour state
  const [settingsTourOpen, setSettingsTourOpen] = useState<boolean>(false);
  const [settingsTourStep, setSettingsTourStep] = useState<number>(0);
  const [settingsTourFlagsChecked, setSettingsTourFlagsChecked] = useState<boolean>(false);
  const [settingsTourShouldOpen, setSettingsTourShouldOpen] = useState<boolean>(false);
  const [settingsTourSeen, setSettingsTourSeen] = useState<boolean>(false);
  const tourFallbackRef = useRef<HTMLElement | null>(null);
  const settingStatusTimersRef = useRef<Partial<Record<SettingStatusKey, ReturnType<typeof setTimeout>>>>({});

  const canEditRules: boolean = useMemo<boolean>(() => selectedRuleLanguage.length > 0, [selectedRuleLanguage]);

  // Disable settings if not authenticated
  const isAuthenticated: boolean = Boolean(userID);
  const settingsDataReady = useMemo<boolean>(
    () => isAuthenticated && !isLoadingInitial,
    [isAuthenticated, isLoadingInitial]
  );

  const markSettingStatus = useCallback((key: SettingStatusKey, status: SettingStatus) => {
    const existingTimer = settingStatusTimersRef.current[key];
    if (existingTimer) {
      clearTimeout(existingTimer);
      delete settingStatusTimersRef.current[key];
    }

    setSettingStatuses((current) => ({ ...current, [key]: status }));

    if (status !== 'saving') {
      settingStatusTimersRef.current[key] = setTimeout(() => {
        setSettingStatuses((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
        delete settingStatusTimersRef.current[key];
      }, 2600);
    }
  }, []);

  useEffect(() => {
    return () => {
      Object.values(settingStatusTimersRef.current).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
      settingStatusTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    setSettingsTourFlagsChecked(false);
    setSettingsTourSeen(false);
    setSettingsTourShouldOpen(false);
    setSettingsTourOpen(false);
    setSettingsTourStep(0);
  }, [userID]);

  // Initialize current email from Firebase auth
  useEffect(() => {
    try {
      const a = getAuth();
      const u = a.currentUser;
      setCurrentEmail(u?.email || '');
      const hasPassword = !!u?.providerData?.some(p => p.providerId === 'password');
      setIsPasswordAccount(hasPassword);
    } catch (_e) {
      // ignore
    }
  }, [userID]);

  // Initial load logic
  useEffect(() => {
    if (!userID) return;

    let mounted = true;
    const loadAll = async () => {
      setIsLoadingInitial(true);
      try {
        const [
          langRes,
          defaultTabRes,
          alwaysRes,
          neverRes,
          flagsRes,
          sagaRes,
          dollarsRes,
          themeRes,
          uiFlagsRes,
        ] = await Promise.all([
          axios.get(`${API_BASE}/get-default-language`, {
            params: { userID }
          }),
          axios.get(`${API_BASE}/get-default-book-tab`, {
            params: { userID }
          }),
          axios.get(`${API_BASE}/get-account-always-translate`, {
            params: { userID }
          }),
          axios.get(`${API_BASE}/get-account-never-translate`, {
            params: { userID }
          }),
          axios.get(`${API_BASE}/check-beta-status/${userID}`),
          axios.get(`${API_BASE}/check-saga-preference/${userID}`),
          axios.get(`${API_BASE}/check-dollar-preference/${userID}`),
          axios.get(`${API_BASE}/check-theme-preference/${userID}`),
          axios.get(`${API_BASE}/user/ui-flags`, {
            params: { userId: userID }
          }).catch(() => ({ data: {} })),
        ]);

        if (!mounted) return;

        const fetchedDefault = langRes.data?.defaultLanguage ?? '';
        const fetchedDefaultModeRaw = langRes.data?.defaultLanguageMode;
        const fetchedDefaultMode: DefaultLanguageMode =
          fetchedDefaultModeRaw === 'empty' || fetchedDefaultModeRaw === 'context' || fetchedDefaultModeRaw === 'specific'
            ? fetchedDefaultModeRaw
            : 'specific';
        const fetchedDefaultTab = defaultTabRes.data?.defaultBookTab;
        setDefaultLanguage(
          LANGUAGE_CONFIG.baseLanguages.includes(fetchedDefault) ? fetchedDefault : ''
        );
        setDefaultLanguageMode(fetchedDefaultMode);
        setDefaultBookTab(
          fetchedDefaultTab === 'metadata' || fetchedDefaultTab === 'dashboard'
            ? fetchedDefaultTab
            : 'dashboard'
        );
        setAlwaysWords(alwaysRes.data?.alwaysTranslateWords ?? {});
        setNeverWords(neverRes.data?.neverTranslateWords ?? {});
        setShowTranslatedFlags(flagsRes.data?.showTranslatedFlags ?? true);
        setShowSagaRaven(sagaRes.data?.showSagaRaven ?? true);
        try {
          localStorage.setItem('showSagaRaven', String(sagaRes.data?.showSagaRaven ?? true));
        } catch (_e) {
          // ignore localStorage failures
        }
        const serverDollarPref = dollarsRes.data?.showDollarEquivalents;
        const initialDollar = (serverDollarPref === undefined || serverDollarPref === null)
          ? shouldShowDollarEquivalents()
          : Boolean(serverDollarPref);
        setShowDollarEquivalents(initialDollar);
        try {
          localStorage.setItem('showDollarEquivalents', String(initialDollar));
        } catch (_e) {}

        const darkModePref = Boolean(themeRes.data?.darkModeEnabled);
        setMode(darkModePref ? 'dark' : 'light');
        const uiFlags = uiFlagsRes.data?.uiFlags || {};
        const systemFlags = uiFlagsRes.data?.systemFlags || {};
        const preflightEnabled =
          typeof systemFlags[BULK_TERM_SCAN_PREFLIGHT_SYSTEM_FLAG] === 'boolean'
            ? Boolean(systemFlags[BULK_TERM_SCAN_PREFLIGHT_SYSTEM_FLAG])
            : true;
        const autoRunTermScan =
          preflightEnabled &&
          typeof uiFlags[TRANSLATION_AUTORUN_TERM_SCAN_FLAG] === 'boolean'
            ? Boolean(uiFlags[TRANSLATION_AUTORUN_TERM_SCAN_FLAG])
            : false;
        const preferAdvancedWorkflow =
          typeof uiFlags[TRANSLATION_WORKFLOW_PREF_FLAG] === 'boolean'
            ? Boolean(uiFlags[TRANSLATION_WORKFLOW_PREF_FLAG])
            : false;
        const requireCharacterAnalysisDefault =
          typeof uiFlags[TRANSLATION_REQUIRE_CHARACTER_ANALYSIS_FLAG] === 'boolean'
            ? Boolean(uiFlags[TRANSLATION_REQUIRE_CHARACTER_ANALYSIS_FLAG])
            : true;
        const simpleModeDefault =
          typeof uiFlags[SIMPLE_MODE_FLAG] === 'boolean'
            ? Boolean(uiFlags[SIMPLE_MODE_FLAG])
            : false;
        setBulkTermScanPreflightEnabled(preflightEnabled);
        setTranslationWorkflowPreferAdvanced(preferAdvancedWorkflow);
        setTranslationRequireCharacterAnalysis(requireCharacterAnalysisDefault);
        setTranslationAutoRunTermScanBeforeBulk(autoRunTermScan);
        setSimpleModeEnabled(simpleModeDefault);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load settings:', err);
      } finally {
        if (mounted) setIsLoadingInitial(false);
      }
    };

    loadAll();
    return () => {
      mounted = false;
    };
  }, [userID, setMode]);

  useEffect(() => {
    if (!userID) return;
    if (settingsTourFlagsChecked) return;
    let cancelled = false;

    const fetchTourFlags = async () => {
      try {
        const res = await axios.get(`${API_BASE}/user/ui-flags`, {
          params: { userId: userID }
        });
        if (cancelled) return;
        const seen = !!res.data?.uiFlags?.accountSettingsTourSeen;
        setSettingsTourSeen(seen);
        if (!seen) {
          setSettingsTourShouldOpen(true);
        }
      } catch (_err) {
        if (cancelled) return;
        setSettingsTourSeen(false);
      } finally {
        if (!cancelled) {
          setSettingsTourFlagsChecked(true);
        }
      }
    };

    fetchTourFlags();
    return () => {
      cancelled = true;
    };
  }, [userID, settingsTourFlagsChecked]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pending = window.sessionStorage.getItem(ACCOUNT_SETTINGS_TOUR_STORAGE_KEY);
    if (pending) {
      window.sessionStorage.removeItem(ACCOUNT_SETTINGS_TOUR_STORAGE_KEY);
      setSettingsTourShouldOpen(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleTourRequest = () => {
      setSettingsTourShouldOpen(true);
    };
    window.addEventListener(ACCOUNT_SETTINGS_TOUR_EVENT, handleTourRequest as EventListener);
    return () => {
      window.removeEventListener(ACCOUNT_SETTINGS_TOUR_EVENT, handleTourRequest as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!settingsTourFlagsChecked) return;
    if (!settingsTourShouldOpen) return;
    if (!settingsDataReady) return;
    setSettingsTourOpen(true);
    setSettingsTourStep(0);
    setSettingsTourShouldOpen(false);
  }, [settingsTourFlagsChecked, settingsTourShouldOpen, settingsDataReady]);

  const resolveTourTarget = useCallback(
    (element?: HTMLElement | null): HTMLElement => {
      if (element) return element;
      if (typeof document !== 'undefined' && document.body) {
        return document.body;
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current =
          typeof document !== 'undefined' ? document.createElement('div') : ({} as HTMLElement);
      }
      return tourFallbackRef.current as HTMLElement;
    },
    []
  );

  const getTourElement = useCallback((id: string) => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null;
  }, []);

  const renderTeacherPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );

  const handleSettingsTourChange = useCallback((nextStep: number) => {
    setSettingsTourStep(nextStep);
  }, []);

  const handleSettingsTourClose = useCallback(async () => {
    setSettingsTourOpen(false);
    setSettingsTourStep(0);
    setSettingsTourShouldOpen(false);
    if (!settingsTourSeen) {
      setSettingsTourSeen(true);
      if (userID) {
        try {
          await axios.post(`${API_BASE}/user/ui-flags/set`, {
            userId: userID,
            flags: { accountSettingsTourSeen: true }
          });
        } catch (_err) {
          // Non-blocking — failure just means we may show the tour again later.
        }
      }
    }
  }, [settingsTourSeen, userID]);

  const settingsTourSteps = useMemo<TourProps['steps']>(() => {
    const targetOrFallback = (primaryId: string, secondaryId?: string) =>
      resolveTourTarget(
        getTourElement(primaryId) ?? (secondaryId ? getTourElement(secondaryId) : null)
      );

    return [
      {
        title: 'Set your default language',
        description: renderTeacherPanel(
          <>
            <Text strong>Choose the language new translations default to.</Text>
            <br />
            <Text type="secondary">
              This affects new work and defaults only. Existing translated editions keep their current languages, and you can still pick a different target language per book.
            </Text>
          </>
        ),
        target: () => targetOrFallback('settings-default-language')
      },
      {
        title: 'Choose your default book tab',
        description: renderTeacherPanel(
          <>
            <Text strong>Control where each book opens first.</Text>
            <br />
            <Text type="secondary">
              Set Dashboard or Metadata as your default landing tab when opening books.
            </Text>
          </>
        ),
        target: () => targetOrFallback('settings-default-book-tab')
      },
      {
        title: 'Create translation rules',
        description: renderTeacherPanel(
          <>
            <Text strong>Keep key phrases consistent.</Text>
            <br />
            <Text type="secondary">
              Manage “always” and “never” translate lists per language so names and terms stay
              correct.
            </Text>
          </>
        ),
        target: () => targetOrFallback('settings-translation-rules')
      },
      {
        title: 'Set glossary auto-run default',
        description: renderTeacherPanel(
          <>
            <Text strong>Control AI Glossary Scan behavior for bulk translations.</Text>
            <br />
            <Text type="secondary">
              This sets the default Translate Book checkbox that runs preflight and auto-applies high-confidence glossary rules.
            </Text>
          </>
        ),
        target: () => targetOrFallback('settings-auto-run-glossary')
      },
      {
        title: 'Tune your workspace',
        description: renderTeacherPanel(
          <>
            <Text strong>Control dark mode, Saga the Raven, and helpful visuals.</Text>
            <br />
            <Text type="secondary">
              Toggle UI helpers to match the way you like to work.
            </Text>
          </>
        ),
        target: () => targetOrFallback('settings-display')
      },
      {
        title: 'Manage your account email',
        description: renderTeacherPanel(
          <>
            <Text strong>Update the email tied to your account.</Text>
            <br />
            <Text type="secondary">
              We send a verification link to the new address and sign you out until the change is confirmed.
            </Text>
          </>
        ),
        target: () => targetOrFallback('settings-account-email')
      },
      {
        title: 'Reset tutorials anytime',
        description: renderTeacherPanel(
          <>
            <Text strong>Need a refresher?</Text>
            <br />
            <Text type="secondary">
              Reset all in-app tours so we can guide you through features again. This does not change your content.
            </Text>
          </>
        ),
        target: () => targetOrFallback('settings-tutorials')
      }
    ];
  }, [getTourElement, renderTeacherPanel, resolveTourTarget]);

  const persistDefaultLanguagePreference = async (
    language: string,
    mode: DefaultLanguageMode,
    statusKey: 'defaultLanguageMode' | 'specificLanguage',
  ) => {
    if (!userID) return;
    markSettingStatus(statusKey, 'saving');
    setIsUpdatingLanguage(true);
    try {
      await axios.post(`${API_BASE}/update-default-language`, {
        userID,
        language,
        defaultLanguageMode: mode,
      });
      setDefaultLanguage(language);
      setDefaultLanguageMode(mode);
      markSettingStatus(statusKey, 'saved');
      message.success('Default language updated successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update default language:', error);
      markSettingStatus(statusKey, 'error');
      message.error('Failed to update default language');
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  // Handle language change
  const handleDefaultLanguageChange = async (value: string) => {
    if (defaultLanguageMode !== 'specific') return;
    await persistDefaultLanguagePreference(value, defaultLanguageMode, 'specificLanguage');
  };

  const handleDefaultLanguageModeChange = async (value: string) => {
    const nextMode: DefaultLanguageMode =
      value === 'empty' || value === 'context' || value === 'specific'
        ? value
        : 'specific';
    await persistDefaultLanguagePreference(defaultLanguage, nextMode, 'defaultLanguageMode');
  };

  const handleDefaultBookTabChange = async (value: string) => {
    if (!userID) return;
    markSettingStatus('defaultBookTab', 'saving');
    setIsUpdatingDefaultBookTab(true);
    try {
      await axios.post(`${API_BASE}/update-default-book-tab`, {
        userID,
        defaultBookTab: value
      });
      setDefaultBookTab(value);
      markSettingStatus('defaultBookTab', 'saved');
      message.success('Default book tab updated successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update default book tab:', error);
      markSettingStatus('defaultBookTab', 'error');
      message.error('Failed to update default book tab');
    } finally {
      setIsUpdatingDefaultBookTab(false);
    }
  };

  // Handle dollar equivalents setting change
  const handleDollarSettingChange = async (checked: boolean) => {
    if (!userID) return;
    markSettingStatus('dollar', 'saving');
    setIsUpdatingDollar(true);
    try {
      await axios.post(
        `${API_BASE}/update-dollar-preference/${userID}`,
        { showDollarEquivalents: checked }
      );
      setShowDollarEquivalents(checked);
      try { localStorage.setItem('showDollarEquivalents', String(checked)); } catch (_e) {}
      markSettingStatus('dollar', 'saved');
      message.success('Dollar equivalents setting updated successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update dollar equivalents setting:', error);
      markSettingStatus('dollar', 'error');
      message.error('Failed to update setting');
      setShowDollarEquivalents(!checked); // revert on error
    } finally {
      setIsUpdatingDollar(false);
    }
  };

  const handleTranslationWorkflowModeDefaultChange = async (checked: boolean) => {
    if (!userID) return;
    markSettingStatus('translationWorkflow', 'saving');
    setIsUpdatingWorkflowModeDefault(true);
    try {
      await axios.post(`${API_BASE}/user/ui-flags/set`, {
        userId: userID,
        flags: {
          [TRANSLATION_WORKFLOW_PREF_FLAG]: Boolean(checked),
        },
      });
      setTranslationWorkflowPreferAdvanced(Boolean(checked));
      markSettingStatus('translationWorkflow', 'saved');
      message.success('Translate Book default view updated');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update Translate Book default view:', error);
      markSettingStatus('translationWorkflow', 'error');
      message.error('Failed to update setting');
    } finally {
      setIsUpdatingWorkflowModeDefault(false);
    }
  };

  const handleRequireCharacterAnalysisDefaultChange = async (checked: boolean) => {
    if (!userID) return;
    markSettingStatus('characterAnalysis', 'saving');
    setIsUpdatingCharacterAnalysisDefault(true);
    try {
      await axios.post(`${API_BASE}/user/ui-flags/set`, {
        userId: userID,
        flags: {
          [TRANSLATION_REQUIRE_CHARACTER_ANALYSIS_FLAG]: Boolean(checked),
        },
      });
      setTranslationRequireCharacterAnalysis(Boolean(checked));
      markSettingStatus('characterAnalysis', 'saved');
      message.success('Character analysis default updated');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update character analysis default:', error);
      markSettingStatus('characterAnalysis', 'error');
      message.error('Failed to update setting');
    } finally {
      setIsUpdatingCharacterAnalysisDefault(false);
    }
  };

  const handleTermScanAutoRunSettingChange = async (checked: boolean) => {
    if (!userID) return;
    if (!bulkTermScanPreflightEnabled) {
      setTranslationAutoRunTermScanBeforeBulk(false);
      return;
    }
    markSettingStatus('termScan', 'saving');
    setIsUpdatingTermScanAutoRun(true);
    try {
      await axios.post(`${API_BASE}/user/ui-flags/set`, {
        userId: userID,
        flags: {
          [TRANSLATION_AUTORUN_TERM_SCAN_FLAG]: Boolean(checked),
        },
      });
      setTranslationAutoRunTermScanBeforeBulk(Boolean(checked));
      markSettingStatus('termScan', 'saved');
      message.success('AI Glossary Scan auto-run default updated');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update AI Glossary Scan auto-run setting:', error);
      markSettingStatus('termScan', 'error');
      message.error('Failed to update setting');
    } finally {
      setIsUpdatingTermScanAutoRun(false);
    }
  };

  const handleSimpleModeChange = async (checked: boolean) => {
    if (!userID) return;
    markSettingStatus('simpleMode', 'saving');
    setIsUpdatingSimpleMode(true);
    try {
      await axios.post(`${API_BASE}/user/ui-flags/set`, {
        userId: userID,
        flags: {
          [SIMPLE_MODE_FLAG]: Boolean(checked),
        },
      });
      setSimpleModeEnabled(Boolean(checked));
      markSettingStatus('simpleMode', 'saved');
      message.success(checked ? 'Simple mode enabled' : 'Simple mode disabled');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update simple mode setting:', error);
      markSettingStatus('simpleMode', 'error');
      message.error('Failed to update setting');
    } finally {
      setIsUpdatingSimpleMode(false);
    }
  };

  const handleThemeModeChange = useCallback(async (checked: boolean) => {
    const desiredMode = checked ? 'dark' : 'light';

    if (!userID) {
      setMode(desiredMode);
      return;
    }

    const previousMode = isDark ? 'dark' : 'light';
    setMode(desiredMode);
    markSettingStatus('theme', 'saving');
    setIsUpdatingTheme(true);
    try {
      await axios.post(
        `${API_BASE}/update-theme-preference/${userID}`,
        { darkModeEnabled: checked }
      );
      markSettingStatus('theme', 'saved');
      message.success('Theme preference updated successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update theme preference:', error);
      markSettingStatus('theme', 'error');
      message.error('Failed to update theme preference');
      setMode(previousMode);
    } finally {
      setIsUpdatingTheme(false);
    }
  }, [userID, setMode, isDark, markSettingStatus]);

  // Handle flags setting change
  const handleFlagsSettingChange = async (checked: boolean) => {
    if (!userID) return;
    markSettingStatus('flags', 'saving');
    setIsUpdatingFlags(true);
    try {
      await axios.post(
        `${API_BASE}/update-translated-flags-setting/${userID}`,
        { showTranslatedFlags: checked }
      );
      setShowTranslatedFlags(checked);
      markSettingStatus('flags', 'saved');
      message.success('Language status display updated');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update flags setting:', error);
      markSettingStatus('flags', 'error');
      message.error('Failed to update setting');
      setShowTranslatedFlags(!checked); // revert on error
    } finally {
      setIsUpdatingFlags(false);
    }
  };

  // Handle Saga Raven setting change
  const handleSagaSettingChange = async (checked: boolean) => {
    if (!userID) return;
    markSettingStatus('saga', 'saving');
    setIsUpdatingSaga(true);
    try {
      await axios.post(
        `${API_BASE}/update-saga-preference/${userID}`,
        { showSagaRaven: checked }
      );
      setShowSagaRaven(checked);
      try {
        localStorage.setItem('showSagaRaven', String(checked));
      } catch (_e) {
        // ignore localStorage failures
      }
      markSettingStatus('saga', 'saved');
      message.success('Saga Raven setting updated successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update saga setting:', error);
      markSettingStatus('saga', 'error');
      message.error('Failed to update setting');
      setShowSagaRaven(!checked); // revert on error
    } finally {
      setIsUpdatingSaga(false);
    }
  };

  const handleResetTutorials = async () => {
    if (!userID) return;
    Modal.confirm({
      title: 'Reset all tutorials?',
      content: 'This will reset all in-app tutorials.',
      okText: 'Reset',
      okType: 'danger',
      onOk: async () => {
        markSettingStatus('tutorials', 'saving');
        setIsResettingTutorials(true);
        try {
          await axios.post(`${API_BASE}/user/ui-flags/reset`, {
            userId: userID
          });
          // Reset per-feature tour flags used by the characters registry tours.
          try {
            await axios.post(`${API_BASE}/user/ui-flags/set`, {
              userId: userID,
              flags: {
                charactersControlsTourSeen: false,
                charactersAnalysisTourSeen: false
              }
            });
          } catch (_e) {
            // Non-fatal; legacy reset endpoint covers older flags
          }
          // Also clear new per-feature tour flags used by the app (Cover History)
          try {
            await axios.post(`${API_BASE}/api/users/${encodeURIComponent(userID)}/flags/tourSeen_coverHistory`, {
              value: false
            });
          } catch (_e) {
            // Non-fatal; legacy reset endpoint covers older flags
          }
          try {
            localStorage.removeItem(`tourFlags:${userID}`);
          } catch (_e) {
            // ignore localStorage failures
          }
          message.success('Tutorials have been reset');
          markSettingStatus('tutorials', 'saved');
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to reset tutorials:', e);
          markSettingStatus('tutorials', 'error');
          message.error('Failed to reset tutorials');
        } finally {
          setIsResettingTutorials(false);
        }
      }
    });
  };

  // Handle sending Firebase verification link to change email (verifyBeforeUpdateEmail)
  const handleSendEmailVerificationLink = async () => {
    if (!isAuthenticated) return;

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmed = (newEmail || '').trim();
    if (!emailPattern.test(trimmed)) {
      markSettingStatus('email', 'error');
      message.error('Enter a valid email address');
      return;
    }

    markSettingStatus('email', 'saving');
    setIsUpdatingEmail(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        markSettingStatus('email', 'error');
        message.error('You must be signed in to change your email');
        return;
      }

      // Use Firebase client flow: sends a verification link to the NEW email.
      // Email address will only change after the link is completed.
      await verifyBeforeUpdateEmail(user, trimmed, {
        url: window.location.origin, // After completion, user is redirected here
        handleCodeInApp: false      // Use Firebase hosted handler (no custom handler required)
      });

      await signOut(auth);
      markSettingStatus('email', 'saved');
      message.success('Verification link sent. You have been signed out. Check your email to verify, then sign back in with your new email.');
      setNewEmail('');
      navigate('/manage-books', { replace: true });
    } catch (err: any) {
      // Common errors
      const code = err?.code || '';
      if (code === 'auth/requires-recent-login') {
        markSettingStatus('email', 'error');
        message.error('Recent login required. Please sign out and sign back in, then try again.');
      } else if (code === 'auth/email-already-in-use') {
        markSettingStatus('email', 'error');
        message.error('That email is already in use.');
      } else if (code === 'auth/invalid-email') {
        markSettingStatus('email', 'error');
        message.error('That email address is invalid.');
      } else {
        // eslint-disable-next-line no-console
        console.error('verifyBeforeUpdateEmail failed:', err);
        markSettingStatus('email', 'error');
        message.error('Failed to send verification link');
      }
    } finally {
      setIsUpdatingEmail(false);
    }
  };


  return (
    <>
      <div className="account-settings-shell">
        <div className="account-settings-save-hint" aria-live="polite">
          <span className="account-settings-save-dot" />
          <Text type="secondary">Settings save automatically.</Text>
        </div>

        {!isAuthenticated && (
          <Alert
            type="info"
            showIcon
            message="You are not signed in"
            description="Sign in to make and save changes."
            className="account-settings-alert"
          />
        )}

        <SettingsSection
          kicker="Defaults"
          title="Starting preferences"
          description="Choose the defaults ScribeShadow should apply when you open books and start translation work."
          icon={<BulbOutlined />}
          tourId="settings-default-language"
        >
          <div className="account-settings-two-column">
            <SettingsSelectRow
              title="Language dropdown behavior"
              description="Choose how translation language dropdowns should initialize."
              status={settingStatuses.defaultLanguageMode}
              tooltip={(
                <div>
                  <div><strong>Always start empty:</strong> No language is preselected. You must choose one before continuing.</div>
                  <div><strong>Use current context:</strong> Preselects based on where you launched from.</div>
                  <div><strong>Always use specific language:</strong> Always preselects the language saved below.</div>
                </div>
              )}
              spinning={isUpdatingLanguage || isLoadingInitial}
            >
              <Select
                className="account-settings-select"
                placeholder="Select default language behavior"
                onChange={handleDefaultLanguageModeChange}
                value={defaultLanguageMode}
                disabled={!isAuthenticated || isUpdatingLanguage || isLoadingInitial}
                size="large"
              >
                <Option value="empty">Always start empty</Option>
                <Option value="context">Use current context</Option>
                <Option value="specific">Always use specific language</Option>
              </Select>
            </SettingsSelectRow>

            <SettingsSelectRow
              title="Specific default language"
              description="Used only when language dropdown behavior is set to a specific language."
              helper={defaultLanguageMode !== 'specific' ? 'Specific language selection is disabled for this mode.' : undefined}
              status={settingStatuses.specificLanguage}
              variant={defaultLanguageMode !== 'specific' ? 'muted' : 'default'}
              spinning={isUpdatingLanguage || isLoadingInitial}
            >
              <Select
                className="account-settings-select"
                placeholder="Select default language"
                onChange={handleDefaultLanguageChange}
                value={defaultLanguage}
                disabled={
                  !isAuthenticated ||
                  isUpdatingLanguage ||
                  isLoadingInitial ||
                  defaultLanguageMode !== 'specific'
                }
                size="large"
                optionFilterProp="children"
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                <Option value="">None</Option>
                {baseLanguages.map((language: string) => (
                  <Option key={language} value={language}>
                    {language}
                  </Option>
                ))}
              </Select>
            </SettingsSelectRow>
          </div>

          <SettingsSelectRow
            title="Default book tab"
            description="Choose what opens first when you visit a book."
            tooltip="This applies when opening a book and when entering edit mode."
            status={settingStatuses.defaultBookTab}
            spinning={isUpdatingDefaultBookTab || isLoadingInitial}
            tourId="settings-default-book-tab"
          >
            <Select
              className="account-settings-select"
              onChange={handleDefaultBookTabChange}
              value={defaultBookTab}
              disabled={!isAuthenticated || isUpdatingDefaultBookTab || isLoadingInitial}
              size="large"
            >
              <Option value="dashboard">Dashboard</Option>
              <Option value="metadata">Metadata</Option>
            </Select>
          </SettingsSelectRow>
        </SettingsSection>

        <SettingsSection
          kicker="Translation"
          title="Translation controls"
          description="Set account-wide terminology rules and Translate Book defaults."
          icon={<TranslationOutlined />}
          tourId="settings-translation-rules"
        >
          <SettingsGroup
            title="Global glossary rules"
            description="Manage account-level terms that should always or never translate."
          >
            <RulesManagerPanel
              baseLanguages={baseLanguages}
              selectedRuleLanguage={selectedRuleLanguage}
              setSelectedRuleLanguage={setSelectedRuleLanguage}
              canEditRules={canEditRules}
              isAuthenticated={isAuthenticated}
              setShowAlwaysModal={setShowAlwaysModal}
              setShowNeverModal={setShowNeverModal}
            />
          </SettingsGroup>

          <SettingsGroup
            title="Translate Book defaults"
            description="Choose which Translate Book options should be enabled when the workflow opens."
          >
            <SettingsSwitchRow
              title="Use Advanced Settings by default for Translate Book"
              description="Open Translate Book in Fine-tune settings instead of Quick setup."
              status={settingStatuses.translationWorkflow}
              checked={translationWorkflowPreferAdvanced}
              onChange={handleTranslationWorkflowModeDefaultChange}
              disabled={!isAuthenticated || isLoadingInitial}
              spinning={isUpdatingWorkflowModeDefault || isLoadingInitial}
              ariaLabel="Use Advanced Settings by default for Translate Book"
            />

            <SettingsSwitchRow
              title="Require character analysis by default"
              description="Sets the default Translate Book checkbox for requiring character analysis before translating."
              status={settingStatuses.characterAnalysis}
              checked={translationRequireCharacterAnalysis}
              onChange={handleRequireCharacterAnalysisDefaultChange}
              disabled={!isAuthenticated || isLoadingInitial}
              spinning={isUpdatingCharacterAnalysisDefault || isLoadingInitial}
              ariaLabel="Require character analysis by default"
            />

            <SettingsSwitchRow
              title="Auto-run a new AI Glossary Scan for bulk translation"
              description="Sets the default Translate Book checkbox. Saved Always/Never Translate rules are used either way."
              helper={!bulkTermScanPreflightEnabled ? 'Disabled system-wide.' : 'Runs a new scan and auto-applies confident suggestions before bulk translations.'}
              status={settingStatuses.termScan}
              variant={!bulkTermScanPreflightEnabled ? 'warning' : 'default'}
              checked={translationAutoRunTermScanBeforeBulk}
              onChange={handleTermScanAutoRunSettingChange}
              disabled={!isAuthenticated || isLoadingInitial || !bulkTermScanPreflightEnabled}
              spinning={isUpdatingTermScanAutoRun || isLoadingInitial}
              ariaLabel="Auto-run a new AI Glossary Scan for bulk translation"
              tourId="settings-auto-run-glossary"
            />
          </SettingsGroup>
        </SettingsSection>

        <SettingsSection
          kicker="Display"
          title="Workspace display"
          description="Tune visual helpers and workspace presentation."
          icon={<FlagOutlined />}
          tourId="settings-display"
        >
          <SettingsSwitchRow
            title="Simple mode"
            description="Replace the full book workspace with a minimal upload, translate, and export flow."
            icon={<BulbOutlined />}
            status={settingStatuses.simpleMode}
            checked={simpleModeEnabled}
            onChange={handleSimpleModeChange}
            disabled={!isAuthenticated || isLoadingInitial}
            spinning={isUpdatingSimpleMode || isLoadingInitial}
            ariaLabel="Simple mode"
          />

          <SettingsSwitchRow
            title="Dark mode"
            description="Switch to the darker interface palette."
            status={settingStatuses.theme}
            checked={isDark}
            onChange={handleThemeModeChange}
            disabled={!isAuthenticated || isLoadingInitial || isUpdatingTheme}
            spinning={isUpdatingTheme || isLoadingInitial}
            ariaLabel="Dark mode"
          />

          <SettingsSwitchRow
            title="Show Saga the Raven assistant"
            description="Display the animated assistant in the UI."
            status={settingStatuses.saga}
            checked={showSagaRaven}
            onChange={handleSagaSettingChange}
            disabled={!isAuthenticated || isLoadingInitial}
            spinning={isUpdatingSaga || isLoadingInitial}
            ariaLabel="Show Saga the Raven assistant"
          />

          <SettingsSwitchRow
            title="Use full language flag graphics on My Books"
            description="When off, My Books shows compact completion chips instead of the full flag grid."
            status={settingStatuses.flags}
            checked={showTranslatedFlags}
            onChange={handleFlagsSettingChange}
            disabled={!isAuthenticated || isLoadingInitial}
            spinning={isUpdatingFlags || isLoadingInitial}
            ariaLabel="Use full language flag graphics on My Books"
          />

          {SHOW_DOLLAR_EQUIVALENTS_SETTING && (
            <SettingsSwitchRow
              title="Show $ equivalents"
              description="Show approximate dollar amounts alongside credit costs. This is an estimate based on your plan."
              status={settingStatuses.dollar}
              checked={showDollarEquivalents}
              onChange={handleDollarSettingChange}
              disabled={!isAuthenticated || isLoadingInitial}
              spinning={isUpdatingDollar || isLoadingInitial}
              ariaLabel="Show $ equivalents"
            />
          )}
        </SettingsSection>

        <div className="account-settings-two-column account-settings-two-column--bottom">
          <SettingsSection
            kicker="Access"
            title="Account access"
            description="Change the email used to sign in."
            tourId="settings-account-email"
            compact
          >
            <AccountEmailPanel
              currentEmail={currentEmail}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              isAuthenticated={isAuthenticated}
              isPasswordAccount={isPasswordAccount}
              isUpdatingEmail={isUpdatingEmail}
              isLoadingInitial={isLoadingInitial}
              emailStatus={settingStatuses.email}
              handleSendEmailVerificationLink={handleSendEmailVerificationLink}
            />
          </SettingsSection>

          <SettingsSection
            kicker="Help"
            title="Tutorials"
            description="Reset guided tours when you want to see onboarding again."
            tourId="settings-tutorials"
            compact
          >
            <TutorialsPanel
              isAuthenticated={isAuthenticated}
              isLoadingInitial={isLoadingInitial}
              isResettingTutorials={isResettingTutorials}
              tutorialsStatus={settingStatuses.tutorials}
              handleResetTutorials={handleResetTutorials}
            />
          </SettingsSection>
        </div>
      </div>

      {/* Modals that edit the account-level dictionaries */}
      <AppTour
        open={settingsTourOpen}
        steps={settingsTourSteps}
        current={settingsTourStep}
        onChange={handleSettingsTourChange}
        onClose={handleSettingsTourClose}
        onFinish={handleSettingsTourClose}
      />

      <AccountAlwaysTranslateModal
        isVisible={showAlwaysModal}
        onClose={() => setShowAlwaysModal(false)}
        currentLanguage={selectedRuleLanguage}
        alwaysTranslateWords={alwaysWords}
        setAlwaysTranslateWords={setAlwaysWords}
        userID={userID}
      />

      <AccountNeverTranslateModal
        isVisible={showNeverModal}
        onClose={() => setShowNeverModal(false)}
        currentLanguage={selectedRuleLanguage}
        neverTranslateWords={neverWords}
        setNeverTranslateWords={setNeverWords}
        userID={userID}
      />
    </>
  );
}
